import React, { useEffect, useRef } from "react";

import Color from "@mapmycustomers/shared/enum/Color";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";

import { useMap } from "@app/component/map/FeaturedMap";
import anyColorToHex from "@app/util/colors/anyColorToHex";

interface Props {
  color: AnyColor | undefined;
  onCreatePolygon?: (paths: LongLat[][]) => void;
  opacity: number;
}

const TerritoryDrawer: React.FC<Props> = ({ color, onCreatePolygon, opacity }) => {
  const { map } = useMap();
  const drawingManager = useRef(
    new google.maps.drawing.DrawingManager({ drawingControl: false, map })
  );

  // visibility effect
  useEffect(() => () => drawingManager.current.setMap(null), []);

  // look'n'feel effect
  useEffect(() => {
    drawingManager.current.setOptions({
      drawingControl: false,
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      polygonOptions: {
        fillColor: anyColorToHex(color ?? Color.BLACK),
        fillOpacity: opacity,
        strokeColor: anyColorToHex(color ?? Color.BLACK),
        strokeOpacity: 1,
        strokeWeight: 2,
      },
    });
  }, [color, opacity]);

  // events effect
  useEffect(() => {
    const listener = google.maps.event.addListener(
      drawingManager.current,
      "polygoncomplete",
      (polygon: google.maps.Polygon) => {
        // convert google paths in LongLat[][]
        const paths: LongLat[][] = [];
        polygon.getPaths().forEach((path) => {
          const coordinates: LongLat[] = [];
          path.forEach((latLng) => {
            coordinates.push([latLng.lng(), latLng.lat()]);
          });
          paths.push(coordinates);
        });

        // hide this polygon from the map
        polygon.setMap(null);

        onCreatePolygon?.(paths);
      }
    );

    return () => {
      listener.remove();
    };
  }, [onCreatePolygon]);

  return null;
};

export default TerritoryDrawer;
